import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, Instagram } from "lucide-react";
import { site } from "@/data/site";

const LINES = ["YOUR BODY IS", "PART OF THE", "MACHINE."];
const EASE = [0.22, 1, 0.36, 1];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const tagY = useTransform(scrollYProgress, [0, 1], ["0%", "160%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      ref={ref}
      id="hero"
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 z-0">
        <img
          src={site.images.hero}
          alt=""
          className="h-[125%] w-full object-cover object-center grayscale-[25%]"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#08080A]/95 via-[#08080A]/60 to-[#08080A]/15" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#08080A] via-transparent to-[#08080A]/60" />
      </motion.div>
      <div className="halftone pointer-events-none absolute inset-0 z-0" />

      <motion.div
        style={{ y: tagY }}
        className="pointer-events-none absolute right-6 top-24 z-[5] hidden -rotate-6 lg:block"
      >
        <p className="font-display text-xl font-black italic uppercase text-purple drop-shadow-[0_0_18px_rgba(168,50,255,0.8)]">
          Built for
        </p>
        <p className="font-display text-3xl font-black italic uppercase text-gold drop-shadow-[0_0_18px_rgba(255,215,0,0.7)]">
          Race Day
        </p>
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-6 pt-28 lg:px-12">
        <motion.p
          initial={{ opacity: 0, x: -24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
          className="mb-6 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.3em] text-gold"
          data-testid="hero-eyebrow"
        >
          <span className="h-px w-10 bg-gold" />
          Motorsport Strength & Conditioning
        </motion.p>

        <h1 className="font-display font-black uppercase leading-[0.95] tracking-tighter">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className={`block text-5xl sm:text-7xl lg:text-8xl ${
                  i === 2
                    ? "text-purple drop-shadow-[0_0_25px_rgba(168,50,255,0.55)]"
                    : "text-white"
                }`}
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.25 + i * 0.14, ease: EASE }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
          className="mt-8 max-w-xl text-base font-medium leading-relaxed text-white/75 lg:text-lg"
          data-testid="hero-subtext"
        >
          Motorsport-specific strength and conditioning for drivers. Programs
          built around your race calendar, tested with real performance data,
          and adjusted as your season changes.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.0, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => document.querySelector("#programs")?.scrollIntoView({ behavior: "smooth" })}
            className="group flex items-center gap-3 border-2 border-purple bg-purple px-7 py-4 text-sm font-black uppercase tracking-[0.15em] text-white transition-all duration-200 hover:-translate-y-1 hover:bg-transparent hover:shadow-[0_0_25px_rgba(168,50,255,0.6)]"
            data-testid="hero-cta-programs"
          >
            See The Programs
            <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <a
            href={site.igUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-3 border-2 border-white/25 bg-transparent px-7 py-4 text-sm font-black uppercase tracking-[0.15em] text-white transition-all duration-200 hover:-translate-y-1 hover:border-gold hover:text-gold hover:shadow-[0_0_25px_rgba(255,215,0,0.35)]"
            data-testid="hero-cta-instagram"
          >
            <Instagram className="h-4 w-4" />
            DM {site.igHandle}
          </a>
        </motion.div>

      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.5 }}
        className="absolute bottom-8 left-6 z-10 hidden items-center gap-3 sm:flex lg:left-12"
      >
        <ArrowDown className="h-4 w-4 animate-bounce text-gold" />
        <span className="text-[10px] font-bold uppercase tracking-[0.35em] text-white/40">
          Scroll
        </span>
      </motion.div>
    </section>
  );
}
